import BaseModal from "../../../components/ui/BaseModal";
import BaseSelect from "../../../components/ui/BaseSelect";
import BaseButton from "../../../components/ui/BaseButton";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { CurrencySymbol, PaymentType } from "../../../utils/enum";
import { useEffect, useLayoutEffect, useState } from "react";
import { fetchNearbyRateComparison } from "../../../services/api/user";
import BaseLoader from "../../../components/ui/BaseLoader";
import CountUp from "react-countup";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseDropdown from "../../../components/ui/BaseDropdown";

const currencies = Object.keys(CurrencySymbol);

const RateComparisonModal = ({ open, buttonRef, isEditMode, setOpenRateComparison }) => {
  const payment = useSelector((state) => state.workOrder.payment);
  const workOrderDetail = useSelector((state) => state.workOrder);
  const [isRefetching, setIsRefetching] = useState(false);
  const [distance, setDistance] = useState(50);

  const paymentType = payment?.pay?.engineerPaymentType;

  const [dropdownStyle, setDropdownStyle] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [currency, setCurrency] = useState(null);

  const createLat = workOrderDetail?.scheduling?.location?.latitude;
  const createLng = workOrderDetail?.scheduling?.location?.longitude;

  const editLat = workOrderDetail?.workOrderDetails?.address?.latitude;
  const editLng = workOrderDetail?.workOrderDetails?.address?.longitude;

  const latitude = isEditMode ? editLat : createLat;
  const longitude = isEditMode ? editLng : createLng;

  const {
    data,
    isLoading: isRateComparisonLoading,
    refetch,
  } = useQuery({
    queryKey: ["rateComparison", distance, paymentType, latitude, longitude],
    queryFn: () =>
      fetchNearbyRateComparison({
        distance,
        paymentType,
        latitude,
        longitude,
        currency,
      }),
    enabled: open && !!paymentType && !!latitude,
  });

  const rates = data || {};
  useEffect(() => {
    setCurrency(data?.selectedCurrency || (rates?.currencies?.[0] ?? null));
  }, [data, rates?.currencies]);

  useEffect(() => {
    if (!currency) return;
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  }, [currency]);

  useLayoutEffect(() => {
    if (open && buttonRef?.current) {
      setIsReady(true);
    } else {
      setDropdownStyle(null);
      setIsReady(false);
    }
  }, [open, buttonRef]);

  if (!open || !isReady || !dropdownStyle) return null;

  return (
    <BaseDropdown
      setOpen={setOpenRateComparison}
      open={open && dropdownStyle !== null}
      className={`${!isEditMode ? "!w-[750px]" : "!w-[590px]"} h-[260px] mt-7`}
      style={{ ...dropdownStyle, boxShadow: "0 1px 10px rgba(0,0,0,0.2)" }}
    >
      <div className="px-6 py-5 space-y-5">
        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <p className="font-medium mb-1">Distance</p>
            <BaseAutoSelect
              name="distance"
              value={distance}
              onChange={(e) => setDistance(Number(e.target.value))}
              options={[
                { label: "50 km", value: 50 },
                { label: "100 km", value: 100 },
                { label: "150 km", value: 150 },
              ]}
            />
          </div>

          <div>
            <p className="font-medium mb-1">Currency</p>
            <BaseAutoSelect
              name="currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              options={(rates?.currencies || []).map((c) => ({
                label: c,
                value: c,
              }))}
              disabled={rates?.currencies?.length === 1}
            />
          </div>
        </div>

        <div className={`grid grid-cols-3 ${isEditMode ? "!gap-1" : "!gap-8"} items-center`}>
          {[
            { label: "Minimum Rate", value: rates?.minRate },
            { label: "Maximum Rate", value: rates?.maxRate },
            { label: "Average Rate", value: rates?.avgRate },
          ].map((item, index) => (
            <div
              key={index}
              className={`${isEditMode ? "w-[160px]" : "w-[200px]"} border rounded-lg px-4 py-3 text-center cursor-pointer group hover:bg-primary transition-colors duration-300`}
            >
              {isRateComparisonLoading || isRefetching ? (
                <BaseLoader isLoading />
              ) : (
                <>
                  <div className="flex items-end justify-center gap-1">
                    <h1 className="text-2xl font-semibold text-primary group-hover:text-white leading-none">
                      <CountUp start={0} end={item.value || 0} duration={2} separator="," />
                    </h1>
                    <span className="text-xs text-primary  group-hover:text-white whitespace-nowrap">
                      {CurrencySymbol[currency] ?? currency} / {PaymentType[paymentType]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mt-2 group-hover:text-white">{item.label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        <p className="text-primary font-regular">
          The rates are retrieved from real-time analytics and based on the filters applied.
        </p>
      </div>
    </BaseDropdown>
  );
};

export default RateComparisonModal;
